import { useCallback, useEffect, useState } from "react";
import { OnixExportStatus as FirestoreOnixExportStatus } from "reedy-data/models";
import { OnixExportStatus as ApiOnixExportStatus } from "reedy-data/api";
import { useDocumentData } from "react-firebase-hooks/firestore";
import { useApi } from "~/hooks/use-api";
import { useSession } from "~/contexts/auth-context";
import { useAnalytics } from "~/contexts/analytics-context";
import { IProduct } from "~/integrations/firebase/collections";
import { OnixGenerationModalStatus } from "~/pages/project-details/types/types";
import { getOnixExportDocument } from "~/integrations/firebase/collections/onix-exports";
import { logError } from "~/shared/services/error-reporting";

export const useOnixGeneration = () => {
  const api = useApi();
  const { gaEvent } = useAnalytics();
  const [showOnixExportModal, setShowOnixExportModal] = useState(false);
  const [exportId, setExportId] = useState<string | null>(null);
  const [onixUrl, setOnixUrl] = useState<string | null>(null);
  const [generatingStatus, setGeneratingStatus] =
    useState<OnixGenerationModalStatus>(OnixGenerationModalStatus.IDLE);
  const { organisation } = useSession();

  const [onixExport, , error] = useDocumentData(
    exportId ? getOnixExportDocument(exportId) : null,
  );

  useEffect(() => {
    if (onixExport?.status === FirestoreOnixExportStatus.COMPLETED) {
      if (onixExport.fileName) {
        // advances the export state machine when the Firestore export document resolves to completed/error; reacts to async document data, and the status is also driven by user actions
        setGeneratingStatus(OnixGenerationModalStatus.SUCCESS);
        // Build full storage path: orgId + returned path (e.g. "export/file.zip")
        setOnixUrl(`${organisation.id}/${onixExport.fileName}`);
      } else {
        setGeneratingStatus(OnixGenerationModalStatus.ERROR);
      }
    }
    if (onixExport?.status === FirestoreOnixExportStatus.ERROR) {
      setGeneratingStatus(OnixGenerationModalStatus.ERROR);
    }
    if (error) {
      setGeneratingStatus(OnixGenerationModalStatus.ERROR);
    }
  }, [onixExport, error, organisation.id]);

  const handleOnixExportButtonClick = useCallback(
    async (selectedProducts: IProduct[]) => {
      setOnixUrl(null);
      setGeneratingStatus(OnixGenerationModalStatus.REQUESTED);
      setShowOnixExportModal(true);
      gaEvent({
        type: "export_books",
        payload: { books_count: selectedProducts.length, export_type: "onix" },
      });

      try {
        const result = await api.products.exportProducts(
          selectedProducts.map((p) => p.id),
        );
        if (result?.status !== ApiOnixExportStatus.REQUESTED || !result?.id) {
          setGeneratingStatus(OnixGenerationModalStatus.ERROR);
        } else {
          setExportId(result.id);
        }
      } catch (err) {
        logError(err);
        setGeneratingStatus(OnixGenerationModalStatus.ERROR);
      }
    },
    [api.products, gaEvent],
  );

  return {
    showOnixExportModal,
    setShowOnixExportModal,
    handleOnixExportButtonClick,
    generatingStatus,
    setGeneratingStatus,
    onixUrl,
  };
};
